import {
  Adb,
  AdbDaemonTransport,
  AdbSignatureAuthenticator,
  AdbAuthType,
  AdbCommand,
} from "@yume-chan/adb";
import { AdbDaemonWebUsbDeviceManager } from "@yume-chan/adb-daemon-webusb";
import * as AdbCredentialWebModule from "@yume-chan/adb-credential-web";
// Module uses `export default class` but TS 4.x resolves it differently with esModuleInterop
const AdbWebCredentialStore = (AdbCredentialWebModule as any).default ?? AdbCredentialWebModule;
import type { AppDispatch } from "../../../common/state/store";
import {
  setDevice,
  updateDeviceStatus,
  clearDevice,
  setError,
} from "../state/deviceInstallerSlice";

let currentAdb: Adb | null = null;
let pendingConnection: { readable: ReadableStream; writable: WritableStream } | null = null;
let disconnectHandler: ((event: USBConnectionEvent) => void) | null = null;
const credentialStore = new AdbWebCredentialStore();

/**
 * Custom authenticator that re-sends the public key multiple times, giving the
 * user time to tap "Allow USB debugging?" on the device.
 *
 * The default `AdbPublicKeyAuthenticator` only yields once and then returns,
 * which causes an immediate "No authenticator can handle the request" error if
 * the device sends another AUTH Token before the user has accepted the dialog.
 *
 * This authenticator yields up to MAX_PUBLIC_KEY_RETRIES times (~30 s at
 * typical ADB protocol pace), which is enough for most real-world scenarios.
 */
const MAX_PUBLIC_KEY_RETRIES = 30;

/**
 * We build the public key payload using the same internal crypto helpers the
 * library uses. They are loaded once and cached here at module level.
 */
let cryptoHelpers: {
  adbGeneratePublicKey: (privateKey: Uint8Array, output: Uint8Array) => void;
  adbGetPublicKeySize: () => number;
  calculateBase64EncodedLength: (len: number) => [number, number];
  encodeBase64: (input: Uint8Array, output: Uint8Array) => void;
  encodeUtf8: (str: string) => Uint8Array;
} | null = null;

async function loadCryptoHelpers() {
  if (cryptoHelpers) return cryptoHelpers;
  // These internal paths are stable across @yume-chan/adb versions we use.
  // No "exports" field in the package.json so they resolve fine at bundle time.
  const cryptoMod = await import("@yume-chan/adb/esm/daemon/crypto.js");
  const utilsMod = await import("@yume-chan/adb/esm/utils/index.js");
  cryptoHelpers = {
    adbGeneratePublicKey: cryptoMod.adbGeneratePublicKey,
    adbGetPublicKeySize: cryptoMod.adbGetPublicKeySize,
    calculateBase64EncodedLength: utilsMod.calculateBase64EncodedLength,
    encodeBase64: utilsMod.encodeBase64,
    encodeUtf8: utilsMod.encodeUtf8,
  };
  return cryptoHelpers;
}

const AdbPublicKeyWaitAuthenticator: any = async function* (
  store: any,
  getNextRequest: () => Promise<any>,
) {
  const helpers = await loadCryptoHelpers();

  // Get or generate a private key
  let privateKey: any;
  for await (const key of store.iterateKeys()) {
    privateKey = key;
    break;
  }
  if (!privateKey) {
    privateKey = await store.generateKey();
  }

  // Build the public key payload
  const publicKeyLength = helpers.adbGetPublicKeySize();
  const [publicKeyBase64Length] = helpers.calculateBase64EncodedLength(publicKeyLength);

  const nameBuffer: Uint8Array = privateKey.name?.length
    ? helpers.encodeUtf8(privateKey.name)
    : new Uint8Array(0);

  const publicKeyBuffer = new Uint8Array(
    publicKeyBase64Length + (nameBuffer.length ? nameBuffer.length + 1 : 0) + 1,
  );
  helpers.adbGeneratePublicKey(privateKey.buffer, publicKeyBuffer);
  helpers.encodeBase64(publicKeyBuffer.subarray(0, publicKeyLength), publicKeyBuffer);
  if (nameBuffer.length) {
    publicKeyBuffer[publicKeyBase64Length] = 0x20; // space
    publicKeyBuffer.set(nameBuffer, publicKeyBase64Length + 1);
  }

  // Keep re-sending the public key for each AUTH Token the device sends,
  // giving the user time to accept the prompt on the device.
  for (let i = 0; i < MAX_PUBLIC_KEY_RETRIES; i++) {
    const packet = await getNextRequest();
    if (packet.arg0 !== AdbAuthType.Token) {
      return; // Not a token — we can't handle this
    }
    yield {
      command: AdbCommand.Auth,
      arg0: AdbAuthType.PublicKey,
      arg1: 0,
      payload: publicKeyBuffer,
    };
  }
};

/**
 * Custom authenticator list:
 * 1. Try stored keys (signature auth) — instant if the device already trusts us
 * 2. Send public key and wait for user to accept on device (up to ~30 retries)
 */
const AUTHENTICATORS = [AdbSignatureAuthenticator, AdbPublicKeyWaitAuthenticator];

/** Detect USB disconnect errors (transferIn/transferOut failures when device is unplugged) */
function isUsbDisconnectError(err: any): boolean {
  const raw = err?.reason ?? err;
  const msg = String(raw?.message || raw || "");
  return (
    msg.includes("transferIn") ||
    msg.includes("transferOut") ||
    msg.includes("device was disconnected") ||
    msg.includes("transfer error") ||
    msg.includes("LIBUSB_TRANSFER") ||
    (raw instanceof DOMException && raw.name === "NetworkError" && msg.includes("USBDevice"))
  );
}

function setupDisconnectListener(serial: string, dispatch: AppDispatch) {
  cleanupDisconnectListener();
  disconnectHandler = (event: USBConnectionEvent) => {
    if (event.device.serialNumber === serial) {
      const adbRef = currentAdb;
      currentAdb = null;
      cleanupDisconnectListener();
      dispatch(clearDevice());
      // Close the ADB transport to stop pending USB transfers
      if (adbRef) {
        Promise.resolve()
          .then(() => adbRef.close())
          .catch(() => {
            // Device already gone — ignore
          });
      }
    }
  };
  navigator.usb.addEventListener("disconnect", disconnectHandler);
}

function cleanupDisconnectListener() {
  if (disconnectHandler) {
    navigator.usb.removeEventListener("disconnect", disconnectHandler);
    disconnectHandler = null;
  }
}

/** Close any in-flight USB connection that hasn't completed auth yet */
async function closePendingConnection() {
  if (pendingConnection) {
    try {
      // Cancel the readable side and abort the writable side to release the USB interface
      await pendingConnection.readable.cancel().catch(() => undefined);
      await pendingConnection.writable.close().catch(() => undefined);
    } catch {
      // Device may already be gone — ignore
    }
    pendingConnection = null;
  }
}

/**
 * Shared helper: authenticate and connect to an already-obtained USB device.
 * Handles dispatch(setDevice), setupDisconnectListener, ADB auth, and package scan.
 */
async function connectToDevice(
  usbDevice: {
    serial: string;
    name: string | undefined;
    connect: () =>
      | ReadableWritablePair<Uint8Array, Uint8Array>
      | Promise<ReadableWritablePair<Uint8Array, Uint8Array>>;
  },
  dispatch: AppDispatch,
): Promise<void> {
  dispatch(
    setDevice({
      serial: usbDevice.serial,
      productName: usbDevice.name ?? "Unknown device",
      manufacturerName: "",
      status: "connecting",
    }),
  );

  setupDisconnectListener(usbDevice.serial, dispatch);

  dispatch(updateDeviceStatus({ status: "authenticating" }));

  const connection = await usbDevice.connect();
  pendingConnection = connection;
  const transport = await AdbDaemonTransport.authenticate({
    serial: usbDevice.serial,
    connection,
    credentialStore,
    authenticators: AUTHENTICATORS,
  });
  pendingConnection = null;

  currentAdb = new Adb(transport);
  dispatch(updateDeviceStatus({ status: "ready" }));

  const { scanInstalledPackages } = await import("./packageScanner");
  scanInstalledPackages(dispatch).catch(() => {
    // Non-blocking — package scan failure shouldn't break the connection
  });
}

export async function requestAndConnect(dispatch: AppDispatch): Promise<void> {
  // Disconnect existing device if any
  if (currentAdb) {
    await disconnect(dispatch);
  }
  await closePendingConnection();

  try {
    const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
    if (!manager) {
      dispatch(setError("WebUSB is not available in this browser"));
      return;
    }

    const usbDevice = await manager.requestDevice();
    if (!usbDevice) {
      return; // User cancelled the picker
    }

    await connectToDevice(usbDevice, dispatch);
  } catch (err: any) {
    pendingConnection = null;
    // Ignore errors if device was already unplugged during connection
    if (isUsbDisconnectError(err) && !currentAdb) return;

    const message = mapConnectionError(err);
    dispatch(updateDeviceStatus({ status: "error", errorMessage: message }));
    currentAdb = null;
    cleanupDisconnectListener();
  }
}

/**
 * Retry only the ADB auth handshake without re-showing the USB picker.
 * Useful when the device was locked during initial auth and the popup was missed.
 */
export async function retryAuthentication(dispatch: AppDispatch): Promise<void> {
  // Clean up any existing ADB session AND any pending USB connection
  if (currentAdb) {
    await disconnect(dispatch);
  }
  await closePendingConnection();
  cleanupDisconnectListener();

  try {
    const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
    if (!manager) {
      dispatch(setError("WebUSB is not available in this browser"));
      return;
    }

    // Get already-paired devices without showing the picker
    let devices: Awaited<ReturnType<typeof manager.getDevices>>;
    try {
      devices = await manager.getDevices();
    } catch {
      // getDevices() failed — fall back to full flow
      await requestAndConnect(dispatch);
      return;
    }
    if (devices.length === 0) {
      // No paired device — fall back to full flow
      await requestAndConnect(dispatch);
      return;
    }

    // Use the first paired device (most recent)
    const usbDevice = devices[0];

    await connectToDevice(usbDevice, dispatch);
  } catch (err: any) {
    pendingConnection = null;
    if (isUsbDisconnectError(err) && !currentAdb) return;

    const message = mapConnectionError(err);
    // setDevice + updateDeviceStatus to ensure the error is visible even if
    // device was cleared by a prior disconnect()
    if (!currentAdb) {
      dispatch(
        setDevice({
          serial: "unknown",
          productName: "Unknown device",
          manufacturerName: "",
          status: "error",
          errorMessage: message,
        }),
      );
    } else {
      dispatch(updateDeviceStatus({ status: "error", errorMessage: message }));
    }
    currentAdb = null;
    cleanupDisconnectListener();
  }
}

export async function disconnect(dispatch: AppDispatch): Promise<void> {
  cleanupDisconnectListener();
  await closePendingConnection();
  try {
    if (currentAdb) {
      await currentAdb.close();
    }
  } catch {
    // Ignore close errors
  }
  currentAdb = null;
  dispatch(clearDevice());
}

export function getConnection(): Adb | null {
  return currentAdb;
}

function mapConnectionError(err: any): string {
  if (err instanceof DOMException) {
    if (err.name === "SecurityError") {
      return "securityError";
    }
    if (err.name === "NotFoundError") {
      return "notFound";
    }
  }

  const msg = String(err?.message || err);
  if (msg.includes("busy") || msg.includes("LIBUSB_ERROR_BUSY") || msg.includes("already in use")) {
    return "deviceBusy";
  }
  if (msg.includes("Access denied") || msg.includes("LIBUSB_ERROR_ACCESS")) {
    return "accessDenied";
  }

  return msg;
}
